// Ejecuta el schema SQL la primera vez que se abre la app.
import { execute, query } from "./client";
import { isTauri } from "@tauri-apps/api/core";
import { supabase, useSupabase } from "./supabaseClient.js";
import schema from "../../database/schema.sql?raw";

export async function ensureSchema() {
  try {
    // En modo Supabase no aplicamos el esquema SQLite desde el cliente
    if (useSupabase()) {
      await ensureSupabaseBasics();
      return;
    }
    // Evita fallar en navegador externo del preview
    if (!isTauri()) return;

    // Crear el archivo si no existe: el plugin lo crea al conectarse
    await execute("PRAGMA foreign_keys = ON;");
    // Ejecutar el schema por partes (idempotente por IF NOT EXISTS)
    const statements = splitSqlStatements(schema);
    for (const stmt of statements) {
      try {
        await execute(stmt);
      } catch (e) {
        console.warn(
          "Statement falló, continuando:",
          firstLine(stmt),
          e?.message || e
        );
      }
    }
    // Asegurar columnas nuevas en tablas existentes (migraciones ligeras)
    await ensureProductColumns();
    // Asegurar impuestos por defecto acordes a México
    await ensureDefaultTaxesMexico();
    // Generar secreto JWT si no existe
    await ensureJwtSecret();
  } catch (err) {
    console.error("Error aplicando schema SQLite:", err);
  }
}

function splitSqlStatements(sql) {
  const lines = String(sql).split(/\r?\n/);
  const out = [];
  let buf = "";
  let inTrigger = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!inTrigger && /^CREATE\s+TRIGGER/i.test(trimmed)) {
      inTrigger = true;
    }
    buf += line + "\n";
    if (inTrigger) {
      if (/END;?\s*$/i.test(trimmed)) {
        out.push(buf.trim());
        buf = "";
        inTrigger = false;
      }
      continue;
    }
    if (/;\s*$/.test(trimmed)) {
      const s = buf.trim();
      if (s) out.push(s);
      buf = "";
    }
  }
  const rest = buf.trim();
  if (rest) out.push(rest);
  return out.filter(Boolean);
}

function firstLine(stmt) {
  return String(stmt).split(/\r?\n/)[0].slice(0, 140);
}

async function ensureJwtSecret() {
  try {
    if (!isTauri() || useSupabase()) return;
    const rows = await query(
      `SELECT value FROM settings WHERE key = 'jwt_secret'`
    );
    if (rows.length) return;
    // Generar secreto aleatorio en hex
    const buf = new Uint8Array(32);
    crypto.getRandomValues(buf);
    const hex = Array.from(buf)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    await execute(`INSERT INTO settings(key, value) VALUES ('jwt_secret', ?)`, [
      hex,
    ]);
  } catch (e) {
    console.warn("No se pudo asegurar jwt_secret:", e);
  }
}

// Impuestos por defecto para México: IVA 0% y 16%
async function ensureDefaultTaxesMexico() {
  try {
    if (useSupabase()) {
      // Asegurar impuestos y categoría básicos en Supabase si existen las tablas
      try {
        const { data: iva } = await supabase
          .from('taxes')
          .select('id_taxes')
          .eq('name', 'IVA')
          .limit(1);
        if (!iva || !iva.length) {
          await supabase.from('taxes').insert({ name: 'IVA', rate: 0 });
        }
      } catch (e) {}
      try {
        const { data: cat } = await supabase
          .from('categories')
          .select('id_categories')
          .eq('name', 'General')
          .limit(1);
        if (!cat || !cat.length) {
          await supabase.from('categories').insert({ name: 'General' });
        }
      } catch (e) {}
      return;
    }
    const rows = await query(`SELECT name, rate FROM taxes`);
    const names = rows.map(r => String(r.name).toLowerCase());
    const hasIva0 = names.includes('iva') && rows.some(r => String(r.name).toLowerCase() === 'iva' && Number(r.rate) === 0);
    const hasIva16 = names.includes('iva 16%') || rows.some(r => String(r.name).toLowerCase() === 'iva 16%' && Number(r.rate) === 16);
    if (!hasIva0) {
      await execute(`INSERT OR IGNORE INTO taxes(name, rate) VALUES ('IVA', 0)`);
    }
    if (!hasIva16) {
      await execute(`INSERT OR IGNORE INTO taxes(name, rate) VALUES ('IVA 16%', 16)`);
    }
  } catch (e) {
    console.warn('No se pudieron asegurar impuestos por defecto:', e?.message || e);
  }
}

// Migraciones ligeras para la tabla products
async function ensureProductColumns() {
  try {
    if (useSupabase()) return; // No migramos columnas en Supabase desde el cliente
    const cols = await query(`PRAGMA table_info(products)`);
    const names = new Set(cols.map((c) => String(c.name)));

    if (!names.has("discount")) {
      await execute(
        `ALTER TABLE products ADD COLUMN discount REAL NOT NULL DEFAULT 0`
      );
      // Backfill a 0 para filas existentes
      await execute(`UPDATE products SET discount = 0 WHERE discount IS NULL`);
    }

    if (!names.has("created_at")) {
      // Añadir columna de forma compatible (sin NOT NULL ni función en DEFAULT)
      await execute(`ALTER TABLE products ADD COLUMN created_at TEXT`);
      // Rellenar valores existentes con la fecha actual
      await execute(
        `UPDATE products SET created_at = datetime('now') WHERE created_at IS NULL`
      );
    }

    if (!names.has("description")) {
      await execute(`ALTER TABLE products ADD COLUMN description TEXT`);
    }

    if (!names.has("notes")) {
      await execute(`ALTER TABLE products ADD COLUMN notes TEXT`);
    }
  } catch (e) {
    console.warn(
      "No se pudieron asegurar columnas de products:",
      e?.message || e
    );
  }
}

async function ensureSupabaseBasics() {
  try {
    if (!useSupabase() || !supabase) return;
    // Verificación suave de vista clave; registra advertencias si falta
    try {
      await supabase.from('v_inventory_basic').select('id').limit(1);
    } catch (e) {
      console.warn('Vista v_inventory_basic no disponible. Aplica database/schema_supabase.sql en Supabase.');
    }
    // Impuestos y categoría por defecto
    await ensureDefaultTaxesMexico();
  } catch (err) {
    console.warn('ensureSupabaseBasics falló:', err?.message || err);
  }
}
